import { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useLocation } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { format } from 'date-fns';

const fmt  = (n) => new Intl.NumberFormat('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 }).format(n);
const fmtD = (n) => new Intl.NumberFormat('en-US', { style:'currency', currency:'USD' }).format(n);
const today = () => new Date().toISOString().split('T')[0];
const BASE  = process.env.REACT_APP_API_URL || '';
const authFetch = (path, opts={}) => fetch(BASE + path, { ...opts, headers: { ...opts.headers, Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ASSET_TYPES  = ['stock','crypto','etf','real_estate','bonds','other'];
const ASSET_COLORS = { stock:'#1a5fa8', crypto:'#c27c0e', etf:'#2d7a4f', real_estate:'#7a5f28', bonds:'#6b3fa8', other:'#6b6560' };
const ASSET_ICONS  = { stock:'↗', crypto:'₿', etf:'◫', real_estate:'⌂', bonds:'↗', other:'◫' };

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid var(--stone)', borderRadius:8, padding:'10px 14px', boxShadow:'var(--shadow)', fontSize:12.5 }}>
      <div style={{ color:'var(--mid)', marginBottom:4 }}>{label}</div>
      {payload.map(p => <div key={p.dataKey} style={{ color:p.color }}>{p.name}: {fmt(p.value)}</div>)}
    </div>
  );
};

function PriceBadge({ ticker, quotes }) {
  if (!ticker) return null;
  const q = quotes[ticker.toUpperCase()];
  if (!q) return <span style={{ fontSize:11, color:'var(--mid)' }}>—</span>;
  if (q.error) return <span style={{ fontSize:11, color:'var(--mid)' }}>n/a</span>;
  const up = q.change_pct >= 0;
  return (
    <div style={{ textAlign:'right' }}>
      <div style={{ fontSize:13.5, fontWeight:500 }}>{fmtD(q.price)}</div>
      <div style={{ fontSize:11, color: up ? 'var(--green)' : 'var(--red)' }}>{up ? '▲' : '▼'} {Math.abs(q.change_pct).toFixed(2)}%</div>
    </div>
  );
}

export default function InvestmentsPage() {
  const location = useLocation();
  const [tab, setTab]             = useState('overview');
  const isMobile = useIsMobile();
  const [summary, setSummary]     = useState(null);
  const [investments, setInvs]    = useState([]);
  const [quotes, setQuotes]       = useState({});
  const [quotesLoading, setQL]    = useState(false);
  const [schwabStatus, setSS]     = useState(null);
  const [schwabPositions, setSP]  = useState(null);
  const [schwabBalances, setSB]   = useState(null);
  const [schwabLoading, setSL]    = useState(false);
  const [showInvModal, setIM]     = useState(false);
  const [showSnapModal, setSM]    = useState(false);
  const [saving, setSaving]       = useState(false);
  const [invForm, setInvForm] = useState({ date:today(), asset_name:'', asset_type:'stock', ticker:'', action:'buy', quantity:'', price_per_unit:'', amount_invested:'', notes:'' });
  const [snapForm, setSnapForm] = useState({ date:today(), total_value:'', notes:'' });

  const fetchQuotes = async (tickers) => {
    if (!tickers.length) return;
    setQL(true);
    try {
      const r = await authFetch(`/api/market/quotes?tickers=${tickers.join(',')}`);
      const d = await r.json();
      setQuotes(q => ({ ...q, ...(d.quotes || {}) }));
    } catch(e) {} finally { setQL(false); }
  };

  const load = useCallback(async () => {
    const [sum, invs] = await Promise.all([api.getInvestmentSummary(), api.getInvestments()]);
    setSummary(sum); setInvs(invs);
    // Schwab status
    try { const s = await authFetch('/api/market/schwab/status').then(r=>r.json()); setSS(s); } catch(e) {}
    // Quotes for known tickers
    const tickers = [...new Set(invs.filter(i=>i.ticker).map(i=>i.ticker.toUpperCase()))];
    // For crypto, also try Yahoo Finance -USD format
    const cryptoTickers = invs.filter(i=>i.asset_type==='crypto'&&i.ticker).map(i=>i.ticker.toUpperCase()+'-USD');
    fetchQuotes([...new Set([...tickers, ...cryptoTickers])]);
  }, []);

  useEffect(() => {
    load();
    if (new URLSearchParams(location.search).get('schwab') === 'connected') {
      toast.success('Schwab connected!'); setTimeout(fetchSchwabData, 1000);
    }
  }, []);

  const fetchSchwabData = async () => {
    setSL(true);
    try {
      const [posR, balR] = await Promise.all([
        authFetch('/api/market/schwab/positions'), authFetch('/api/market/schwab/balances')
      ]);
      const pos = await posR.json(); const bal = await balR.json();
      if (pos.positions) { setSP(pos); fetchQuotes(pos.positions.filter(p=>p.symbol).map(p=>p.symbol)); }
      if (bal.accounts) setSB(bal);
      if (!posR.ok) toast.error(pos.detail || 'Could not load positions');
    } catch(e) { toast.error(e.message); } finally { setSL(false); }
  };

  const connectSchwab = async () => {
    const r = await authFetch('/api/market/schwab/connect').then(r=>r.json());
    if (r.auth_url) window.location.href = r.auth_url;
    else toast.error(r.message || r.detail || 'Schwab not configured');
  };

  const disconnectSchwab = async () => {
    if (!window.confirm('Disconnect Schwab?')) return;
    await authFetch('/api/market/schwab/disconnect', { method:'POST' });
    setSS(s => ({ ...s, connected:false })); setSP(null); setSB(null);
    toast.success('Disconnected');
  };

  const [editInv, setEditInv] = useState(null); // investment being edited
  const [editForm, setEditForm] = useState({});

  const openEdit = (inv) => {
    setEditInv(inv);
    setEditForm({
      date: inv.date,
      asset_name: inv.asset_name,
      asset_type: inv.asset_type,
      ticker: inv.ticker || '',
      action: inv.action,
      quantity: inv.quantity ?? '',
      price_per_unit: inv.price_per_unit ?? '',
      amount_invested: inv.amount_invested,
      notes: inv.notes || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.updateInvestment(editInv.id, {
        ...editForm,
        quantity: editForm.quantity ? parseFloat(editForm.quantity) : null,
        price_per_unit: editForm.price_per_unit ? parseFloat(editForm.price_per_unit) : null,
        amount_invested: parseFloat(editForm.amount_invested),
        ticker: editForm.ticker || null,
        notes: editForm.notes || null,
      });
      toast.success('Updated!');
      setEditInv(null);
      load();
    } catch(e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const setEF = (k) => (e) => {
    const v = e.target.value;
    setEditForm(f => {
      const u = { ...f, [k]: v };
      if (k==='quantity'||k==='price_per_unit') {
        const q = parseFloat(k==='quantity'?v:f.quantity);
        const p = parseFloat(k==='price_per_unit'?v:f.price_per_unit);
        if (q>0&&p>0) u.amount_invested = (q*p).toFixed(2);
      }
      return u;
    });
  };

  const setIF = (k) => (e) => {
    const v = e.target.value;
    setInvForm(f => {
      const u = { ...f, [k]:v };
      if (k==='quantity'||k==='price_per_unit') { const q=parseFloat(k==='quantity'?v:f.quantity), p=parseFloat(k==='price_per_unit'?v:f.price_per_unit); if(q>0&&p>0) u.amount_invested=(q*p).toFixed(2); }
      return u;
    });
  };

  const saveInvestment = async () => {
    if (!invForm.asset_name || !invForm.amount_invested) return toast.error('Asset name and amount required');
    setSaving(true);
    try {
      await api.addInvestment({ ...invForm, quantity: invForm.quantity ? parseFloat(invForm.quantity) : null, price_per_unit: invForm.price_per_unit ? parseFloat(invForm.price_per_unit) : null, amount_invested: parseFloat(invForm.amount_invested), ticker: invForm.ticker || null, notes: invForm.notes || null });
      toast.success('Logged!'); setIM(false); setInvForm({ date:today(), asset_name:'', asset_type:'stock', ticker:'', action:'buy', quantity:'', price_per_unit:'', amount_invested:'', notes:'' }); load();
    } catch(e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const saveSnapshot = async () => {
    if (!snapForm.total_value) return toast.error('Value required');
    setSaving(true);
    try { await api.addSnapshot({ date:snapForm.date, total_value:parseFloat(snapForm.total_value), notes:snapForm.notes||null }); toast.success('Saved!'); setSM(false); setSnapForm({ date:today(), total_value:'', notes:'' }); load(); }
    catch(e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const pieData = summary ? Object.entries(summary.by_asset_type||{}).map(([name,value])=>({name,value})) : [];
  const chartData = summary?.snapshot_history?.map(s=>({ date:format(new Date(s.date),'MMM d'), value:s.value })) || [];
  const plColor = (summary?.unrealized_pl??0) >= 0 ? 'var(--green)' : 'var(--red)';

  // ── Live crypto P&L (per ticker) ──────────────────────────────────────────
  const cryptoPositions = investments
    .filter(i => i.asset_type === 'crypto' && i.ticker)
    .reduce((acc, i) => {
      const t = i.ticker.toUpperCase();
      if (!acc[t]) acc[t] = { ticker: t, asset_name: i.asset_name, totalQty: 0, totalCost: 0 };
      if (i.action === 'buy') { acc[t].totalQty += (i.quantity || 0); acc[t].totalCost += i.amount_invested; }
      if (i.action === 'sell') { acc[t].totalQty -= (i.quantity || 0); acc[t].totalCost -= i.amount_invested; }
      return acc;
    }, {});

  const cryptoWithPnl = Object.values(cryptoPositions).map(pos => {
    // Try ticker as-is, then with -USD suffix (Yahoo Finance format)
    const q = quotes[pos.ticker] || quotes[pos.ticker + '-USD'] || quotes[pos.ticker.replace('-USD','')];
    const livePrice = q?.price;
    const currentValue = livePrice ? pos.totalQty * livePrice : null;
    const unrealizedPnl = currentValue !== null ? currentValue - pos.totalCost : null;
    const pnlPct = unrealizedPnl !== null && pos.totalCost > 0 ? (unrealizedPnl / pos.totalCost * 100) : null;
    const avgCost = pos.totalQty > 0 ? pos.totalCost / pos.totalQty : null;
    return { ...pos, livePrice, currentValue, unrealizedPnl, pnlPct, avgCost, priceChange: q?.change_pct };
  }).filter(p => p.totalQty > 0);

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-eyebrow">Portfolio</div>
          <div className="page-title">Investments</div>
          <div className="page-subtitle" style={{ marginBottom:0 }}>Capital deployed, live prices, and Schwab account sync</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost" onClick={() => setSM(true)}>+ Update Value</button>
          <button className="btn btn-primary" onClick={() => setIM(true)}>+ Log Transaction</button>
        </div>
      </div>

      {/* Schwab Banner */}
      <div className="card mb-6" style={{ background: schwabStatus?.connected ? 'var(--green-bg)' : 'var(--off)', borderColor: schwabStatus?.connected ? '#b3d9c2' : 'var(--stone)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div style={{ width:36, height:36, borderRadius:8, background:'var(--white)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, border:'1px solid var(--stone)' }}>≋</div>
            <div>
              <div style={{ fontWeight:500, fontSize:14 }}>
                Schwab Account Sync
                {schwabStatus?.connected && <span className="badge badge-green" style={{ marginLeft:8 }}>Connected</span>}
                {!schwabStatus?.connected && schwabStatus?.configured && <span className="badge badge-stone" style={{ marginLeft:8 }}>Not connected</span>}
                {!schwabStatus?.configured && <span className="badge badge-yellow" style={{ marginLeft:8 }}>Setup needed</span>}
              </div>
              <div style={{ fontSize:12, color:'var(--dim)', marginTop:2 }}>
                {schwabStatus?.connected ? 'Read-only access. Cannot trade or move money.' :
                 schwabStatus?.configured ? 'Click Connect to authorize read-only access.' :
                 'Add SCHWAB_CLIENT_ID + SCHWAB_CLIENT_SECRET to Railway to enable.'}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {schwabStatus?.connected && <>
              <button className="btn btn-ghost btn-sm" onClick={fetchSchwabData} disabled={schwabLoading}>{schwabLoading ? 'Syncing…' : '↻ Sync'}</button>
              <button className="btn btn-sm" style={{ color:'var(--red)', border:'1px solid var(--stone)', background:'var(--white)' }} onClick={disconnectSchwab}>Disconnect</button>
            </>}
            {!schwabStatus?.connected && schwabStatus?.configured && <button className="btn btn-primary btn-sm" onClick={connectSchwab}>Connect Schwab →</button>}
            {!schwabStatus?.configured && <a href="https://developer.schwab.com" target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">developer.schwab.com ↗</a>}
          </div>
        </div>

        {!schwabStatus?.configured && (
          <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid var(--stone)' }}>
            <div className="section-title mb-4" style={{ marginBottom:10 }}>Quick Setup (15 min)</div>
            {[
              ['1','Go to developer.schwab.com → sign in with your Schwab credentials → click "Create App"'],
              ['2','App name: "Ledger" · Callback URL: your-railway-url.up.railway.app/api/market/schwab/callback'],
              ['3','Copy your Client ID and Client Secret from the app page'],
              ['4','In Railway → Variables, add SCHWAB_CLIENT_ID, SCHWAB_CLIENT_SECRET, SCHWAB_CALLBACK_URL then redeploy'],
              ['5','Refresh this page and click Connect Schwab →'],
            ].map(([n,t]) => (
              <div key={n} style={{ display:'flex', gap:10, fontSize:12.5, marginBottom:8 }}>
                <div style={{ width:20, height:20, borderRadius:'50%', background:'var(--stone)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:600, flexShrink:0, color:'var(--ink2)' }}>{n}</div>
                <span style={{ color:'var(--dim)', lineHeight:1.5 }}>{t}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='overview'?'active':''}`} onClick={()=>setTab('overview')}>Overview</button>
        <button className={`tab ${tab==='schwab'?'active':''}`} onClick={()=>setTab('schwab')}>
          Schwab Live {schwabPositions && <span style={{ marginLeft:4, fontSize:10, background:'var(--green-bg)', color:'var(--green)', padding:'1px 6px', borderRadius:99 }}>{schwabPositions.position_count}</span>}
        </button>
        <button className={`tab ${tab==='log'?'active':''}`} onClick={()=>setTab('log')}>Transaction Log</button>
      </div>

      {tab==='overview' && summary && <>
        <div className="grid-4 mb-6">
          <div className="card"><div className="stat-label">Net Invested</div><div className="stat-value gold">{fmt(summary.net_invested)}</div><div className="stat-sub">Cash deployed</div></div>
          <div className="card"><div className="stat-label">Portfolio Value</div><div className="stat-value blue">{summary.latest_portfolio_value!=null?fmt(summary.latest_portfolio_value):'—'}</div><div className="stat-sub">{summary.latest_snapshot_date?`As of ${format(new Date(summary.latest_snapshot_date),'MMM d, yyyy')}`:'No snapshot yet'}</div></div>
          <div className="card"><div className="stat-label">Unrealized P&L</div><div className="stat-value" style={{ color:plColor }}>{summary.unrealized_pl!=null?fmt(summary.unrealized_pl):'—'}</div><div className="stat-sub">{summary.pl_pct!=null?`${summary.pl_pct>0?'+':''}${summary.pl_pct.toFixed(1)}%`:''}</div></div>
          <div className="card"><div className="stat-label">Proceeds (Sells)</div><div className="stat-value green">{fmt(summary.total_recovered)}</div><div className="stat-sub">Total realized</div></div>
        </div>

        {/* Live Crypto P&L */}
        {cryptoWithPnl.length > 0 && (
          <div className="card mb-6">
            <div className="section-title mb-4">Live Crypto Positions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: '0 0 8px', borderBottom: '1px solid var(--stone)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)' }}>
                <div>Asset</div>
                {!isMobile && <div style={{ textAlign: 'right' }}>Holdings</div>}
                {!isMobile && <div style={{ textAlign: 'right' }}>Avg Cost</div>}
                <div style={{ textAlign: 'right' }}>Live Price</div>
                <div style={{ textAlign: 'right' }}>Current Value</div>
                <div style={{ textAlign: 'right' }}>Unrealized P&L</div>
              </div>
              {cryptoWithPnl.map(pos => {
                const pnlCol = pos.unrealizedPnl >= 0 ? 'var(--green)' : 'var(--red)';
                const q = quotes[pos.ticker];
                const priceChange = q?.change_pct;
                return (
                  <div key={pos.ticker} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--stone)', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{pos.ticker}</div>
                      <div style={{ fontSize: 12, color: 'var(--mid)' }}>{pos.asset_name}</div>
                    </div>
                    {!isMobile && <div style={{ textAlign: 'right', fontSize: 13 }}>{pos.totalQty.toFixed(6)} {pos.ticker}</div>}
                    {!isMobile && <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--mid)' }}>{pos.avgCost ? fmtD(pos.avgCost) : '—'}</div>}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{pos.livePrice ? fmtD(pos.livePrice) : quotesLoading ? '…' : '—'}</div>
                      {pos.priceChange !== undefined && pos.priceChange !== null && <div style={{ fontSize: 11, color: pos.priceChange >= 0 ? 'var(--green)' : 'var(--red)' }}>{pos.priceChange >= 0 ? '▲' : '▼'} {Math.abs(pos.priceChange).toFixed(2)}%</div>}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 500 }}>
                      {pos.currentValue !== null ? fmt(pos.currentValue) : '—'}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {pos.unrealizedPnl !== null ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600, color: pnlCol }}>{pos.unrealizedPnl >= 0 ? '+' : ''}{fmt(pos.unrealizedPnl)}</div>
                          <div style={{ fontSize: 11, color: pnlCol }}>{pos.pnlPct >= 0 ? '+' : ''}{pos.pnlPct?.toFixed(1)}%</div>
                        </>
                      ) : '—'}
                    </div>
                  </div>
                );
              })}
              {/* Total row */}
              {cryptoWithPnl.length > 1 && (() => {
                const totalCost = cryptoWithPnl.reduce((a, p) => a + p.totalCost, 0);
                const totalVal = cryptoWithPnl.every(p => p.currentValue !== null) ? cryptoWithPnl.reduce((a, p) => a + (p.currentValue || 0), 0) : null;
                const totalPnl = totalVal !== null ? totalVal - totalCost : null;
                const totalPct = totalPnl !== null && totalCost > 0 ? (totalPnl / totalCost * 100) : null;
                const col = totalPnl >= 0 ? 'var(--green)' : 'var(--red)';
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr 1fr 1fr', gap: 12, padding: '12px 0 0', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>Total Crypto</div>
                    {!isMobile && <div/>}{!isMobile && <div/>}{!isMobile && <div/>}
                    <div style={{ textAlign: 'right', fontWeight: 600, fontSize: 13 }}>{totalVal !== null ? fmt(totalVal) : '—'}</div>
                    <div style={{ textAlign: 'right' }}>
                      {totalPnl !== null && <><div style={{ fontSize: 13, fontWeight: 700, color: col }}>{totalPnl >= 0 ? '+' : ''}{fmt(totalPnl)}</div><div style={{ fontSize: 11, color: col }}>{totalPct >= 0 ? '+' : ''}{totalPct?.toFixed(1)}%</div></>}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--stone)' }}>
              Live prices via CoinGecko · refreshed on page load
            </div>
          </div>
        )}

        <div className="grid-2 mb-6">
          <div className="card">
            <div className="section-title mb-4">Portfolio Value Over Time</div>
            {chartData.length > 1
              ? <ResponsiveContainer width="100%" height={180}><AreaChart data={chartData}><defs><linearGradient id="invG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--blue)" stopOpacity={0.15}/><stop offset="95%" stopColor="var(--blue)" stopOpacity={0}/></linearGradient></defs><XAxis dataKey="date" tick={{ fill:'var(--mid)', fontSize:11 }} axisLine={false} tickLine={false}/><YAxis hide/><Tooltip content={<ChartTip/>}/><Area type="monotone" dataKey="value" stroke="var(--blue)" strokeWidth={2} fill="url(#invG)" dot={{ r:3, fill:'var(--blue)' }} name="Portfolio"/></AreaChart></ResponsiveContainer>
              : <div style={{ textAlign:'center', padding:'40px 20px', color:'var(--mid)', fontSize:13 }}>Add snapshots to track growth</div>}
          </div>
          <div className="card">
            <div className="section-title mb-4">Allocation by Asset Type</div>
            {pieData.length > 0
              ? <div style={{ display:'flex', gap:20, alignItems:'center' }}>
                  <PieChart width={140} height={140}><Pie data={pieData} cx={65} cy={65} innerRadius={38} outerRadius={62} dataKey="value" paddingAngle={3}>{pieData.map((d,i)=><Cell key={i} fill={ASSET_COLORS[d.name]||'#94a3b8'}/>)}</Pie></PieChart>
                  <div style={{ flex:1, display:'flex', flexDirection:'column', gap:7 }}>
                    {pieData.map(d=>(
                      <div key={d.name} className="flex justify-between items-center">
                        <div className="flex items-center gap-2"><div style={{ width:8, height:8, borderRadius:'50%', background:ASSET_COLORS[d.name]||'#94a3b8'}}/><span style={{ fontSize:13, textTransform:'capitalize', color:'var(--ink2)' }}>{ASSET_ICONS[d.name]} {d.name}</span></div>
                        <span style={{ fontSize:13, fontWeight:500 }}>{fmt(d.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              : <div style={{ textAlign:'center', padding:'40px 20px', color:'var(--mid)', fontSize:13 }}>No investments yet</div>}
          </div>
        </div>

        {investments.filter(i=>i.ticker).length > 0 && (
          <div className="card" style={{ padding:0 }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--stone)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div className="section-title" style={{ marginBottom:0 }}>Holdings — Live Prices</div>
              {quotesLoading && <span style={{ fontSize:12, color:'var(--mid)' }}>Fetching quotes…</span>}
            </div>
            <table><thead><tr><th>Asset</th><th>Ticker</th><th>Type</th><th style={{ textAlign:'right' }}>Invested</th><th style={{ textAlign:'right' }}>Current Price</th></tr></thead>
            <tbody>{investments.filter(i=>i.action==='buy').map(inv=>(
              <tr key={inv.id}>
                <td style={{ fontWeight:500 }}>{inv.asset_name}</td>
                <td style={{ color:'var(--mid)', fontSize:12 }}>{inv.ticker||'—'}</td>
                <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, background:ASSET_COLORS[inv.asset_type]+'18', color:ASSET_COLORS[inv.asset_type], textTransform:'capitalize' }}>{ASSET_ICONS[inv.asset_type]} {inv.asset_type}</span></td>
                <td className="text-right" style={{ color:'var(--red)' }}>−{fmtD(inv.amount_invested)}</td>
                <td className="text-right"><PriceBadge ticker={inv.ticker} quotes={quotes}/></td>
              </tr>
            ))}</tbody></table>
          </div>
        )}
      </>}

      {tab==='schwab' && (
        <div className="animate-in">
          {!schwabStatus?.connected && (
            <div className="card" style={{ textAlign:'center', padding:'48px', borderStyle:'dashed' }}>
              <div style={{ fontSize:28, marginBottom:10 }}>≋</div>
              <div style={{ fontWeight:500, marginBottom:6 }}>Connect Schwab to see live positions</div>
              <div style={{ color:'var(--mid)', fontSize:13, marginBottom:20 }}>Read-only. Cannot trade or move money.</div>
              {schwabStatus?.configured ? <button className="btn btn-primary" onClick={connectSchwab}>Connect Schwab →</button> : <div style={{ fontSize:12.5, color:'var(--dim)' }}>Complete the setup guide above first</div>}
            </div>
          )}
          {schwabStatus?.connected && !schwabPositions && (
            <div className="card" style={{ textAlign:'center', padding:'40px' }}>
              <button className="btn btn-primary" onClick={fetchSchwabData} disabled={schwabLoading}>{schwabLoading ? 'Loading…' : '↻ Load Schwab Data'}</button>
            </div>
          )}
          {schwabBalances && (
            <div className="grid-3 mb-6">
              <div className="card"><div className="stat-label">Total Account Value</div><div className="stat-value blue">{fmt(schwabBalances.total_value)}</div><div className="stat-sub">{schwabBalances.accounts?.length} account{schwabBalances.accounts?.length!==1?'s':''}</div></div>
              <div className="card"><div className="stat-label">Cash Balance</div><div className="stat-value green">{fmt(schwabBalances.total_cash)}</div><div className="stat-sub">Available cash</div></div>
              {schwabPositions && <div className="card"><div className="stat-label">Unrealized P&L</div><div className="stat-value" style={{ color:schwabPositions.total_unrealized_pl>=0?'var(--green)':'var(--red)' }}>{schwabPositions.total_unrealized_pl>=0?'+':''}{fmt(schwabPositions.total_unrealized_pl)}</div><div className="stat-sub">All positions</div></div>}
            </div>
          )}
          {schwabPositions?.positions?.length > 0 && (
            <div className="card" style={{ padding:0 }}>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--stone)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div className="section-title" style={{ marginBottom:0 }}>Live Positions — {schwabPositions.position_count} holdings</div>
                <button className="btn btn-sm btn-ghost" onClick={fetchSchwabData} disabled={schwabLoading}>{schwabLoading?'Syncing…':'↻ Refresh'}</button>
              </div>
              <table><thead><tr><th>Symbol</th><th>Description</th><th>Qty</th><th style={{ textAlign:'right' }}>Avg Cost</th><th style={{ textAlign:'right' }}>Market Value</th><th style={{ textAlign:'right' }}>Unrealized P&L</th><th style={{ textAlign:'right' }}>Live Price</th><th>Acct</th></tr></thead>
              <tbody>{schwabPositions.positions.map((pos,i)=>(
                <tr key={i}>
                  <td style={{ fontWeight:600 }}>{pos.symbol}</td>
                  <td style={{ color:'var(--dim)', fontSize:12, maxWidth:160 }}><span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pos.description}</span></td>
                  <td style={{ color:'var(--ink2)' }}>{pos.quantity}</td>
                  <td className="text-right" style={{ color:'var(--dim)' }}>{fmtD(pos.avg_price)}</td>
                  <td className="text-right" style={{ fontWeight:500 }}>{fmt(pos.market_value)}</td>
                  <td className="text-right">
                    <div style={{ color:pos.unrealized_pl>=0?'var(--green)':'var(--red)', fontWeight:500 }}>{pos.unrealized_pl>=0?'+':''}{fmtD(pos.unrealized_pl)}</div>
                    <div style={{ fontSize:11, color:pos.pl_pct>=0?'var(--green)':'var(--red)' }}>{pos.pl_pct>=0?'+':''}{pos.pl_pct.toFixed(2)}%</div>
                  </td>
                  <td className="text-right"><PriceBadge ticker={pos.symbol} quotes={quotes}/></td>
                  <td><span className="badge badge-stone">…{pos.account}</span></td>
                </tr>
              ))}</tbody></table>
            </div>
          )}
        </div>
      )}

      {tab==='log' && (
        <div className="card animate-in" style={{ padding:0 }}>
          <table><thead><tr><th>Date</th><th>Asset</th><th>Type</th><th>Action</th><th>Qty</th><th style={{ textAlign:'right' }}>Amount</th><th></th></tr></thead>
          <tbody>{investments.length===0 ? <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'var(--mid)' }}>No investments logged yet</td></tr> : investments.map(inv=>(
            <tr key={inv.id}>
              <td style={{ color:'var(--mid)', fontSize:12 }}>{format(new Date(inv.date),'MMM d, yyyy')}</td>
              <td><div style={{ fontWeight:500 }}>{inv.asset_name}</div>{inv.ticker&&<div style={{ fontSize:11, color:'var(--mid)' }}>{inv.ticker}</div>}</td>
              <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, background:ASSET_COLORS[inv.asset_type]+'18', color:ASSET_COLORS[inv.asset_type], textTransform:'capitalize' }}>{ASSET_ICONS[inv.asset_type]} {inv.asset_type}</span></td>
              <td><span className={`badge ${inv.action==='buy'?'badge-green':'badge-red'}`} style={{ textTransform:'capitalize' }}>{inv.action}</span></td>
              <td style={{ color:'var(--dim)' }}>{inv.quantity??'—'}</td>
              <td className="text-right" style={{ fontWeight:500, color:inv.action==='buy'?'var(--red)':'var(--green)' }}>{inv.action==='buy'?'−':'+'}{fmtD(inv.amount_invested)}</td>
              <td>
                <div className="flex gap-2">
                  <button className="btn btn-sm btn-ghost" onClick={() => openEdit(inv)}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={async()=>{await api.deleteInvestment(inv.id);load();}}>×</button>
                </div>
              </td>
            </tr>
          ))}</tbody></table>
        </div>
      )}

      {/* Add investment modal */}
      {showInvModal && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setIM(false)}>
          <div className="modal">
            <div className="modal-title">Log Investment Transaction</div>
            <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:16 }}>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Date</label><input className="input" type="date" value={invForm.date} onChange={setIF('date')}/></div>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Action</label><select className="input" value={invForm.action} onChange={setIF('action')}><option value="buy">Buy</option><option value="sell">Sell</option></select></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr 1fr':'2fr 1fr 1fr', gap:16, marginTop:16 }}>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Asset Name *</label><input className="input" placeholder="Apple, Bitcoin…" value={invForm.asset_name} onChange={setIF('asset_name')}/></div>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Ticker (for live prices)</label><input className="input" placeholder="AAPL" value={invForm.ticker} onChange={setIF('ticker')}/></div>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Asset Type</label><select className="input" value={invForm.asset_type} onChange={setIF('asset_type')}>{ASSET_TYPES.map(t=><option key={t} value={t}>{ASSET_ICONS[t]} {t.charAt(0).toUpperCase()+t.replace('_',' ').slice(1)}</option>)}</select></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 1fr', gap:16, marginTop:16 }}>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Quantity</label><input className="input" type="number" step="any" placeholder="10" value={invForm.quantity} onChange={setIF('quantity')}/></div>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Price/unit</label><input className="input" type="number" step="any" placeholder="175.00" value={invForm.price_per_unit} onChange={setIF('price_per_unit')}/></div>
              <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">Total Amount ($) *</label><input className="input" type="number" step="0.01" value={invForm.amount_invested} onChange={setIF('amount_invested')}/></div>
            </div>
            <div style={{ fontSize:11, color:'var(--mid)', marginTop:4 }}>Qty × price auto-fills total · Add a ticker to get live prices</div>
            <div className="form-group mt-4"><label className="form-label">Notes</label><input className="input" placeholder="Dollar-cost averaging…" value={invForm.notes} onChange={setIF('notes')}/></div>
            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={()=>setIM(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveInvestment} disabled={saving}>{saving?'Saving…':`Log ${invForm.action==='buy'?'Buy':'Sell'}`}</button>
            </div>
          </div>
        </div>
      )}

      {/* Snapshot modal */}
      {editInv && (
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setEditInv(null)}>
          <div className="modal" style={{ maxWidth:500 }}>
            <div className="modal-title">Edit Investment</div>
            <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:16 }}>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Date</label>
                <input className="input" type="date" value={editForm.date} onChange={setEF('date')}/>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Asset Name</label>
                <input className="input" placeholder="Bitcoin" value={editForm.asset_name} onChange={setEF('asset_name')}/>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Asset Type</label>
                <select className="input" value={editForm.asset_type} onChange={setEF('asset_type')}>
                  {ASSET_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Ticker</label>
                <input className="input" placeholder="BTC-USD, AAPL…" value={editForm.ticker} onChange={setEF('ticker')}/>
                <div style={{ fontSize:11, color:'var(--mid)', marginTop:3 }}>Use BTC-USD for Bitcoin</div>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Action</label>
                <select className="input" value={editForm.action} onChange={setEF('action')}>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Quantity</label>
                <input className="input" type="number" step="any" placeholder="0.005" value={editForm.quantity} onChange={setEF('quantity')}/>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Price per Unit ($)</label>
                <input className="input" type="number" step="any" placeholder="85000" value={editForm.price_per_unit} onChange={setEF('price_per_unit')}/>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Total Amount ($)</label>
                <input className="input" type="number" step="any" value={editForm.amount_invested} onChange={setEF('amount_invested')}/>
              </div>
            </div>
            <div className="form-group mt-4">
              <label className="form-label">Notes</label>
              <input className="input" placeholder="Osmo, Bitkey…" value={editForm.notes} onChange={setEF('notes')}/>
            </div>
            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={()=>setEditInv(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={saving}>{saving?'Saving…':'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
